import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICONS } from '../../icons';

/**
 * Check if URL is an image
 */
const isImageUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)(\?.*)?$/i.test(url);
};

/**
 * Check if URL is a video embed (YouTube or Twitch)
 */
const isVideoEmbed = (url) => {
  if (!url || typeof url !== 'string') return false;

  // YouTube patterns
  if (/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)/.test(url)) {
    return true;
  }

  // Twitch patterns
  if (/twitch\.tv\/videos\/\d+/.test(url) ||
      /clips\.twitch\.tv\/[a-zA-Z0-9_-]+/.test(url) ||
      /twitch\.tv\/[^/]+\/clip\/[a-zA-Z0-9_-]+/.test(url)) {
    return true;
  }

  return false;
};

/**
 * Extracts video/embed info from YouTube/Twitch/Twitter URLs
 */
const getEmbed = (url) => {
  if (!url || typeof url !== 'string') return null;

  // Check if it's an image URL first
  if (isImageUrl(url)) {
    return {
      type: 'image',
      url: url
    };
  }

  // YouTube patterns
  const youtubePatterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/
  ];

  for (const pattern of youtubePatterns) {
    const match = url.match(pattern);
    if (match) {
      return {
        type: 'youtube',
        id: match[1],
        embedUrl: `https://www.youtube.com/embed/${match[1]}`
      };
    }
  }

  // Twitch video pattern
  const twitchVideoMatch = url.match(/twitch\.tv\/videos\/(\d+)/);
  if (twitchVideoMatch) {
    return {
      type: 'twitch',
      id: twitchVideoMatch[1],
      embedUrl: `https://player.twitch.tv/?video=${twitchVideoMatch[1]}&parent=${window.location.hostname}`
    };
  }

  // Twitch clip pattern
  const twitchClipMatch = url.match(/clips\.twitch\.tv\/([a-zA-Z0-9_-]+)/);
  if (twitchClipMatch) {
    return {
      type: 'twitch-clip',
      id: twitchClipMatch[1],
      embedUrl: `https://clips.twitch.tv/embed?clip=${twitchClipMatch[1]}&parent=${window.location.hostname}`
    };
  }

  // Twitch channel clip pattern (twitch.tv/channel/clip/clipname)
  const twitchChannelClipMatch = url.match(/twitch\.tv\/[^/]+\/clip\/([a-zA-Z0-9_-]+)/);
  if (twitchChannelClipMatch) {
    return {
      type: 'twitch-clip',
      id: twitchChannelClipMatch[1],
      embedUrl: `https://clips.twitch.tv/embed?clip=${twitchChannelClipMatch[1]}&parent=${window.location.hostname}`
    };
  }

  // Twitter/X patterns - matches tweets
  const twitterPatterns = [
    /(?:twitter\.com|x\.com)\/([a-zA-Z0-9_]+)\/status\/(\d+)/
  ];

  for (const pattern of twitterPatterns) {
    const match = url.match(pattern);
    if (match) {
      return {
        type: 'twitter',
        user: match[1],
        id: match[2],
        url: url.replace('x.com', 'twitter.com') // Normalize to twitter.com for embed
      };
    }
  }

  return null;
};

/**
 * Video/Media embed component
 */
const MediaEmbed = ({ url }) => {
  const embed = getEmbed(url);
  if (!embed) return null;

  // Image embeds
  if (embed.type === 'image') {
    return (
      <a href={embed.url} target="_blank" rel="noopener noreferrer" className="markdown-image-link">
        <img src={embed.url} alt="" loading="lazy" />
      </a>
    );
  }

  // Twitter embeds use a different approach - link to tweet with preview
  if (embed.type === 'twitter') {
    return (
      <div className="markdown-twitter-embed">
        <blockquote className="twitter-tweet" data-theme="dark">
          <a href={embed.url}>View tweet from @{embed.user}</a>
        </blockquote>
      </div>
    );
  }

  // Video embeds (YouTube, Twitch)
  return (
    <div className="markdown-video-embed">
      <iframe
        src={embed.embedUrl}
        title={`${embed.type} video`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
};

/**
 * Video Carousel component for multiple consecutive videos
 */
const VideoCarousel = ({ urls }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef(null);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? urls.length - 1 : prev - 1));
  }, [urls.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === urls.length - 1 ? 0 : prev + 1));
  }, [urls.length]);

  const goToSlide = useCallback((index) => {
    setCurrentIndex(index);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!carouselRef.current?.contains(document.activeElement)) return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [goToPrevious, goToNext]);

  return (
    <div
      className="video-carousel"
      ref={carouselRef}
      tabIndex={0}
      role="region"
      aria-label="Video carousel"
      aria-roledescription="carousel"
    >
      <div className="video-carousel-viewport">
        <div
          className="video-carousel-track"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {urls.map((url, index) => (
            <div
              key={index}
              className="video-carousel-slide"
              role="group"
              aria-roledescription="slide"
              aria-label={`Slide ${index + 1} of ${urls.length}`}
              aria-hidden={index !== currentIndex}
            >
              <MediaEmbed url={url} />
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        className="video-carousel-arrow video-carousel-arrow-prev"
        onClick={goToPrevious}
        aria-label="Previous video"
      >
        <FontAwesomeIcon icon={ICONS.CHEVRON_LEFT} />
      </button>

      <button
        type="button"
        className="video-carousel-arrow video-carousel-arrow-next"
        onClick={goToNext}
        aria-label="Next video"
      >
        <FontAwesomeIcon icon={ICONS.CHEVRON_RIGHT} />
      </button>

      <div className="video-carousel-counter">
        {currentIndex + 1} / {urls.length}
      </div>

      <div className="video-carousel-dots" role="tablist" aria-label="Slide navigation">
        {urls.map((_, index) => (
          <button
            type="button"
            key={index}
            className={`video-carousel-dot ${index === currentIndex ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
            role="tab"
            aria-selected={index === currentIndex}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};


/**
 * Custom link renderer that can also create embeds
 */
const LinkRenderer = ({ href, children }) => {
  // Check if this is a standalone embeddable link (link text equals URL)
  const childText = React.Children.toArray(children).join('');
  if (childText === href) {
    const embed = getEmbed(href);
    if (embed) {
      return <MediaEmbed url={href} />;
    }
  }

  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
};

/**
 * Renders a segment of markdown content
 */
const MarkdownSegment = ({ content }) => {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ParagraphRenderer,
        a: LinkRenderer,
        img: (props) => (
          <a href={props.src} target="_blank" rel="noopener noreferrer" className="markdown-image-link">
            <img src={props.src} alt={props.alt || ''} loading="lazy" />
          </a>
        )
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

/**
 * Simple paragraph renderer for single videos
 */
const ParagraphRenderer = ({ children }) => {
  const childArray = React.Children.toArray(children);

  if (childArray.length === 1 && typeof childArray[0] === 'string') {
    const text = childArray[0].trim();
    const embed = getEmbed(text);
    if (embed) {
      return <MediaEmbed url={text} />;
    }
  }

  return <p>{children}</p>;
};

/**
 * MarkdownDisplay component - renders markdown content with video embed support
 * @param {Object} props
 * @param {string} props.content - Markdown content to render
 * @param {string} [props.className] - Additional CSS classes
 */
export default function MarkdownDisplay({ content, className = '' }) {
  // Preprocess content to extract video carousels
  const { segments } = useMemo(() => {
    if (!content || typeof content !== 'string') {
      return { segments: [] };
    }

    const lines = content.split('\n');
    const result = [];
    let currentMarkdown = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i].trim();

      // Check if this line is a standalone video URL
      if (isVideoEmbed(line)) {
        // Look ahead for consecutive video URLs
        const videoUrls = [line];
        let j = i + 1;

        while (j < lines.length) {
          const nextLine = lines[j].trim();
          if (nextLine === '') {
            j++;
            continue;
          }
          if (isVideoEmbed(nextLine)) {
            videoUrls.push(nextLine);
            j++;
          } else {
            break;
          }
        }

        // Push any accumulated markdown first
        if (currentMarkdown.length > 0) {
          result.push({ type: 'markdown', content: currentMarkdown.join('\n') });
          currentMarkdown = [];
        }

        if (videoUrls.length >= 2) {
          // Multiple videos - create carousel segment
          result.push({ type: 'carousel', urls: videoUrls });
        } else {
          // Single video - treat as markdown
          result.push({ type: 'markdown', content: lines[i] });
        }

        i = j;
      } else {
        currentMarkdown.push(lines[i]);
        i++;
      }
    }

    // Push any remaining markdown
    if (currentMarkdown.length > 0) {
      result.push({ type: 'markdown', content: currentMarkdown.join('\n') });
    }

    return { segments: result };
  }, [content]);

  if (!content) return null;

  return (
    <div className={`markdown-display ${className}`}>
      {segments.map((segment, index) => {
        if (segment.type === 'carousel') {
          return <VideoCarousel key={index} urls={segment.urls} />;
        }
        return <MarkdownSegment key={index} content={segment.content} />;
      })}
    </div>
  );
}

/**
 * Strips markdown formatting from text (for previews/truncation)
 * @param {string} markdown - Markdown text to strip
 * @returns {string} Plain text without markdown formatting
 */
export function stripMarkdown(markdown) {
  if (!markdown || typeof markdown !== 'string') return markdown || '';

  return markdown
    // Remove headers
    .replace(/^#{1,6}\s+/gm, '')
    // Remove bold/italic
    .replace(/(\*{1,3}|_{1,3})([^*_]+)\1/g, '$2')
    // Remove strikethrough
    .replace(/~~([^~]+)~~/g, '$1')
    // Remove links, keep text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Remove images
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
    // Remove inline code
    .replace(/`([^`]+)`/g, '$1')
    // Remove code blocks
    .replace(/```[\s\S]*?```/g, '')
    // Remove blockquotes
    .replace(/^>\s+/gm, '')
    // Remove horizontal rules
    .replace(/^[-*_]{3,}$/gm, '')
    // Remove list markers
    .replace(/^[\s]*[-*+]\s+/gm, '')
    .replace(/^[\s]*\d+\.\s+/gm, '')
    // Collapse multiple newlines
    .replace(/\n{2,}/g, ' ')
    // Collapse multiple spaces
    .replace(/\s{2,}/g, ' ')
    .trim();
}
